import { brl } from "@/lib/api";

/**
 * A conta do item, do valor da nota ao lucro.
 *
 * Antes disto a informação existia, mas espalhada e condicional: o custo real
 * numa linha, o rateio noutra, o lucro só nas linhas "Criar novo" — e tudo isso
 * escondido quando o frete era zero, que é justamente o caso em que a pessoa
 * quer conferir se o custo bate com a nota.
 *
 * A ordem aqui é a ordem em que a conta acontece, e é a mesma que o contador
 * usa para fechar o custo de aquisição:
 *
 *     valor da nota  +  rateio  −  desconto  =  custo real
 *     preço de venda −  custo real           =  lucro
 *
 * Por unidade E na linha. O unitário é o que decide o preço de etiqueta; o
 * total da linha é o que aparece no contas a pagar e no estoque — e quem
 * confere a nota precisa dos dois.
 */

/** Linha da ficha: rótulo à esquerda, valor alinhado à direita. */
function Linha({ rotulo, valor, detalhe, tom = "text-slate-600", forte = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className={`${tom} ${forte ? "font-medium" : ""}`}>{rotulo}</span>
      <span className="flex items-baseline gap-2 min-w-0">
        {detalhe && <span className="text-[10px] text-slate-400 truncate">{detalhe}</span>}
        <span className={`font-mono tabular-nums shrink-0 ${tom} ${forte ? "font-semibold" : ""}`}>{valor}</span>
      </span>
    </div>
  );
}

export default function ItemEconomics({ r, custoUnit }) {
  const qtd = Math.max(0, Number(r.qty) || 0);
  const tabela = Number(r.unit_value) || 0;
  const rateio = Number(r.allocated_extra) || 0;
  const desconto = Number(r.allocated_discount) || 0;
  const partes = r.cost_parts || {};

  /* O preço que vale para ESTA linha: o digitado quando se cria, o do cadastro
     quando se vincula. Sem o segundo, vincular não mostrava lucro nenhum — e é
     exatamente aí que a pessoa precisa ver se a compra nova estragou a margem
     do preço que já está na etiqueta. */
  const preco = Number(r.action === "create" ? r.price : (r.price ?? r.current_price)) || 0;
  const lucroUnit = preco > 0 ? preco - custoUnit : 0;
  const margem = preco > 0 ? (lucroUnit / preco) * 100 : 0;

  const natureza = [
    partes.frete && `frete ${brl(partes.frete)}`,
    partes.seguro && `seguro ${brl(partes.seguro)}`,
    partes.outros && `desp. ${brl(partes.outros)}`,
    partes.ipi && `IPI ${brl(partes.ipi)}`,
    partes.icms_st && `ST ${brl(partes.icms_st)}`,
  ].filter(Boolean).join(" · ");

  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60 px-2.5 py-2 text-[11px] space-y-1"
      data-testid="stock-import-economics">
      <Linha rotulo="Valor da nota" valor={`${brl(tabela)} /un`} detalhe={qtd > 1 ? `× ${qtd}` : null} />

      {/* Rateio e desconto só aparecem quando existem: uma linha "+ R$ 0,00" a
          cada item empurraria para baixo o que interessa. */}
      {rateio > 0 && (
        <Linha rotulo="+ Rateio" valor={brl(rateio / (qtd || 1))} detalhe={natureza} tom="text-slate-500" />
      )}
      {desconto > 0 && (
        <Linha rotulo="− Desconto" valor={brl(desconto / (qtd || 1))} tom="text-emerald-600" />
      )}

      <div className="border-t border-slate-200 pt-1">
        <Linha rotulo="Custo real" valor={`${brl(custoUnit)} /un`} tom="text-slate-800" forte
          detalhe={qtd > 1 ? `${brl(custoUnit * qtd)} na linha` : null} />
      </div>

      {preco > 0 && (
        <>
          <Linha
            rotulo={r.action === "create" ? "Preço de venda" : "Preço atual"}
            valor={`${brl(preco)} /un`}
            tom="text-slate-700"
          />
          {/* O lucro é o número que decide, então é o único em cor: verde
              quando há margem, vermelho quando o preço está abaixo do custo —
              que é o erro que ninguém percebe digitando 74 preços seguidos. */}
          <Linha
            rotulo="Lucro"
            valor={`${brl(lucroUnit)} /un`}
            detalhe={`${margem.toFixed(0)}% do preço${qtd > 1 ? ` · ${brl(lucroUnit * qtd)} na linha` : ""}`}
            tom={lucroUnit > 0 ? "text-emerald-700" : "text-red-600"}
            forte
          />
        </>
      )}

      {/* Piso: é o que o PDV recusa furar no desconto. Fica no fim porque é
          consequência do preço, não parte da formação do custo. */}
      {Number(r.min_price) > 0 && (
        <Linha rotulo="Piso de venda" valor={brl(r.min_price)} tom="text-slate-400" />
      )}
    </div>
  );
}
